// W3C trace propagation, and spans for the transport the browser SDK does not
// instrument on its own. Adapted from go-schedule's src/lib/tracing.ts for
// the fsgnext router (/graphql instead of /query).
//
// @sentry/vue emits `sentry-trace` and `baggage` and has no option to emit
// `traceparent`, so left alone the trace stops at the network boundary: the
// browser reports one trace, the backend starts an unrelated one. Every
// GraphQL request therefore carries a `traceparent` built here from the span
// it belongs to — even with SENTRY_DSN unset (see src/boot/sentry.ts), in
// which case @sentry/vue's no-op client still accepts these calls harmlessly,
// so this exchange needs no guard of its own.
import { subgraphErrorMessage, nestedSubgraphMessages } from 'src/core/subgraphError'
import * as Sentry from '@sentry/vue'
import {
  getOperationName,
  makeOperation,
  mapExchange,
  type CombinedError,
  type Exchange,
  type Operation,
} from '@urql/vue'
import type { GraphQLError } from 'graphql'

// SpanStatus codes. @sentry/browser re-exports neither the constants nor the
// enum, so they are spelled out; the values are OpenTelemetry's.
const STATUS_OK = 1
const STATUS_ERROR = 2

const TRACE_FLAG_SAMPLED = 0x1

/** A span's identity as a W3C `traceparent` header (version 00). */
export function traceparent(span: Sentry.Span): string {
  const { traceId, spanId, traceFlags } = span.spanContext()
  return `00-${traceId}-${spanId}-${traceFlags & TRACE_FLAG_SAMPLED ? '01' : '00'}`
}

/** The document's operation name — what the span is called in the trace. */
function operationName(op: Operation): string {
  // A persisted document is an id, not a document, and has no name to read.
  const name = 'definitions' in op.query ? getOperationName(op.query) : undefined
  return name || 'anonymous'
}

// ── reporting GraphQL failures ────────────────────────────────────────────────
//
// A GraphQL error rides in the payload of an HTTP 200, so nothing reports it
// on its own — not the SDK's http instrumentation, not an unhandled
// rejection (urql hands the error back as data), and not the replay.

/**
 * The one code that means "somebody has to look at this". Everything else the
 * backend emits (FORBIDDEN, BAD_USER_INPUT, and gqlgen's own GRAPHQL_*
 * refusals) names an outcome that was decided on purpose.
 */
const CODE_INTERNAL = 'INTERNAL_ERROR'

/**
 * An error with no code is reported. It means the answer came from something
 * that does not publish one — an older backend, a proxy's error page — and
 * "unrecognised" is the case that should be loud.
 */
function isFault(err: GraphQLError): boolean {
  const code = err.extensions?.code
  return typeof code !== 'string' || code === CODE_INTERNAL
}

/** A GraphQL error path as a tag value: names only, list indices dropped. */
export function errorPath(path: GraphQLError['path']): string {
  return (path ?? []).filter((p): p is string => typeof p === 'string').join('.')
}

/** The code, as the tag value the backend's span attribute uses. */
function errorCode(err: GraphQLError): string {
  const code = err.extensions?.code
  return typeof code === 'string' && code ? code : 'none'
}

/**
 * A real Error for a GraphQL fault, because what urql hands back is not one.
 *
 * A non-Error is stored by Sentry as an `extra` and titled by its keys, so
 * every backend failure would otherwise arrive as an indistinguishable
 * "Object captured as exception with keys: extensions, locations, message,
 * path" — no type, no message, no stack. The message was in the payload the
 * whole time; it just has to be on an Error for Sentry to use it.
 */
export function graphqlFault(err: GraphQLError, operation: string, reason?: string): Error {
  const path = errorPath(err.path)
  const where = path ? `${operation}.${path}` : operation
  // `reason` is the subgraph's own message where the router wrapped one — it
  // is what makes the Sentry title actionable (#97). Falls back to the
  // error's message for callers that have not unwrapped.
  const fault = new Error(`${where}: ${reason ?? err.message}`)
  // Not "Error" — this is the browser's report of a server-side failure.
  fault.name = 'GraphQLError'
  return fault
}

/** Collapse digit runs so two instances of the same failure on different ids
 *  group as one issue rather than one per id. */
export function redactNumbers(msg: string): string {
  return msg.replace(/\d+/g, '#').slice(0, 160)
}

/**
 * Report the faults in a failed result, linked to the operation's span.
 *
 * `networkError` is deliberately not reported here: a dropped request is
 * usually connectivity, the SDK's own http.client span already records it,
 * and a backend 5xx is reported by the backend. Payload errors are the class
 * that nothing else sees.
 */
function reportFaults(error: CombinedError, op: Operation, span: Sentry.Span | undefined): void {
  const faults = error.graphQLErrors.filter(isFault)
  if (faults.length === 0) return

  const name = operationName(op)
  Sentry.withActiveSpan(span ?? null, (scope) => {
    for (const fault of faults) {
      const path = errorPath(fault.path)
      scope.setTag('graphql.operation', name)
      scope.setTag('graphql.path', path)
      scope.setTag('graphql.error.code', errorCode(fault))
      // Report the SUBGRAPH's reason, not the router's envelope. Everything
      // beneath the router arrives as "Failed to fetch from Subgraph '<name>'"
      // with the reason nested in extensions.errors — which Sentry renders as
      // `errors: "[Array]"`, so the issue title and the context were both
      // unactionable. #97 spent hours on a question this payload already
      // answered. The nested reason also drives the fingerprint, so a
      // permission denial and a subgraph outage stop grouping as one issue.
      const reason = subgraphErrorMessage(fault)
      const nested = nestedSubgraphMessages(fault)
      scope.setFingerprint(['graphql', name, path, redactNumbers(reason)])
      scope.setContext('graphql', {
        operation: name,
        path,
        code: errorCode(fault),
        message: fault.message,
        reason,
        subgraphErrors: nested.length > 0 ? nested.join(' | ') : undefined,
        locations: fault.locations,
        extensions: fault.extensions,
      })
      Sentry.captureException(graphqlFault(fault, name, reason))
    }
  })
}

/**
 * One Sentry span per GraphQL operation, named after the operation.
 *
 * Belongs AFTER the cacheExchange: an operation answered from the cache never
 * reaches the network, and a span for it would time nothing.
 */
export function graphqlTracingExchange(): Exchange {
  const spans = new Map<number, Sentry.Span>()

  const finish = (key: number, status: Parameters<Sentry.Span['setStatus']>[0]) => {
    const span = spans.get(key)
    if (!span) return
    spans.delete(key)
    span.setStatus(status)
    span.end()
  }

  // A span that never ends is never sent, and this map is the only reference
  // to it — cover the tab closing mid-request.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      for (const key of [...spans.keys()]) finish(key, { code: STATUS_ERROR, message: 'cancelled' })
    })
  }

  return mapExchange({
    onOperation(op) {
      // A cancelled operation never produces a result, so its span has to be
      // closed here or the map grows for the life of the page.
      if (op.kind === 'teardown') {
        finish(op.key, { code: STATUS_ERROR, message: 'cancelled' })
        return
      }

      const name = operationName(op)
      const detached = !Sentry.getActiveSpan()
      const span = Sentry.startInactiveSpan({
        name: `${op.kind} ${name}`,
        op: `graphql.${op.kind}`,
        attributes: {
          'graphql.document.name': name,
          'graphql.operation.type': op.kind,
          'graphql.detached': detached,
        },
      })
      // A second execution of the same key while the first is still open
      // would otherwise replace it and lose it.
      finish(op.key, { code: STATUS_ERROR, message: 'cancelled' })
      spans.set(op.key, span)

      // Merge into the client's fetchOptions rather than replace them — they
      // carry the auth token and the impersonation headers — and stay a
      // function, so the token is still read at request time.
      const base = op.context.fetchOptions
      const header = traceparent(span)
      return makeOperation(op.kind, op, {
        ...op.context,
        fetchOptions: () => {
          const opts = (typeof base === 'function' ? base() : base) || {}
          const headers = new Headers(opts.headers)
          headers.set('traceparent', header)
          return { ...opts, headers }
        },
      })
    },

    onResult(result) {
      // Before finish, which drops the span from the map: the report has to
      // be made against the span it belongs to.
      if (result.error)
        reportFaults(result.error, result.operation, spans.get(result.operation.key))
      finish(
        result.operation.key,
        result.error ? { code: STATUS_ERROR, message: 'internal_error' } : { code: STATUS_OK },
      )
    },
  })
}
